import React, { useCallback, useRef, useState } from "react";
import { useGlobalData } from "../context/GlobalDataContext";
import { Link } from "react-router-dom";
import "./ThreeTableSection.css";


function ThreeTableSection() {
  const {
    threeTable,
    loading,
    fetchThreeTableSection,
    threeTableHasMore
  } = useGlobalData();
  // Only show approved status data for all tables
  // Only show jobs with showOnHome === true in public section
  const jobs = (threeTable.jobs || []).filter(job => job.showOnHome === true);
  const results = (threeTable.results || []).filter(item => item.status === "approved");
  const admitCards = (threeTable.admitCards || []).filter(card => card.status === "approved");
  const answerKeys = (threeTable.answerKeys || []).filter(item => item.status === "approved");
  const syllabuses = (threeTable.syllabuses || []).filter(item => item.status === "approved");
  const admissions = (threeTable.admissions || []).filter(item => item.status === "approved");
  const [inlineLoadingBySection, setInlineLoadingBySection] = useState({
    jobs: false,
    results: false,
    admitCards: false,
    answerKeys: false,
    syllabuses: false,
    admissions: false
  });

  // ✅ Refs for scroll boxes
  const jobsScrollRef = useRef(null);
  const resultsScrollRef = useRef(null);
  const admitCardsScrollRef = useRef(null);
  const answerKeysScrollRef = useRef(null);
  const syllabusesScrollRef = useRef(null);
  const admissionsScrollRef = useRef(null);

  React.useEffect(() => {
    const navEntry = performance.getEntriesByType("navigation")?.[0];
    const isReload = navEntry?.type === "reload";

    if (!isReload) return;

    setTimeout(() => {
      if (jobsScrollRef.current) jobsScrollRef.current.scrollTop = 0;
      if (resultsScrollRef.current) resultsScrollRef.current.scrollTop = 0;
      if (admitCardsScrollRef.current) admitCardsScrollRef.current.scrollTop = 0;
      if (answerKeysScrollRef.current) answerKeysScrollRef.current.scrollTop = 0;
      if (syllabusesScrollRef.current) syllabusesScrollRef.current.scrollTop = 0;
      if (admissionsScrollRef.current) admissionsScrollRef.current.scrollTop = 0;
    }, 100);
  }, []);

  const getMFormSlug = (item) => item?.jobSlug || item?.slug || "";
  const getJobTitle = (jobSlug) => jobs.find((job) => job.slug === jobSlug)?.title || jobSlug;

  const setInlineLoading = useCallback((section, value) => {
    setInlineLoadingBySection((prev) => {
      if (prev[section] === value) return prev;
      return { ...prev, [section]: value };
    });
  }, []);

  const handleTableScroll = useCallback(
    async (section, target) => {
      if (!target || inlineLoadingBySection[section]) return;

      const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
      if (!nearBottom) return;

      if (!threeTableHasMore[section]) return;

      setInlineLoading(section, true);
      try {
        await fetchThreeTableSection(section, false, {
          preferLoadedData: true,
          fetchIfPartial: true,
          showLoading: false,
          skipLoader: true
        });
      } finally {
        setInlineLoading(section, false);
      }
    },
    [fetchThreeTableSection, inlineLoadingBySection, threeTableHasMore, setInlineLoading]
  );

  if (loading) {
    return (
      <div className="three-table-section-grid">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="table-card">
            <div className="table-header" style={{backgroundColor: '#e5e7eb', borderRadius: '4px', height: '24px', animation: 'shimmer 3s infinite', marginBottom: '12px'}}></div>
            <div className="table-content" style={{minHeight: '200px'}}>
              <ul>
                {[...Array(5)].map((_, j) => (
                  <li key={j} style={{backgroundColor: '#f3f4f6', borderRadius: '4px', height: '16px', marginBottom: '8px', animation: 'shimmer 3s infinite'}}></li>
                ))}
              </ul>
            </div>
            <div className="table-footer" style={{backgroundColor: '#e5e7eb', borderRadius: '4px', height: '20px', animation: 'shimmer 3s infinite', marginTop: '12px'}}></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="three-table-section-grid">
      {/* ✅ Latest Jobs */}
      <div className="table-card">
        <Link to="/jobTablePage" className="table-header-link">
          <div className="table-header">Latest Jobs</div>
        </Link>
        <div
          className="table-content scroll-box"
          ref={jobsScrollRef}
          onScroll={(e) => handleTableScroll("jobs", e.currentTarget)}
        >
          <ul>
            {jobs.map((item, i) => (
              <li key={i}>
                <Link to={`/mform/${item.slug}`}>{item.title}</Link>
              </li>
            ))}
            {inlineLoadingBySection.jobs && (
              <li className="table-inline-loading">Loading more jobs...</li>
            )}
            {!threeTableHasMore.jobs && !inlineLoadingBySection.jobs && jobs.length > 0 && (
              <li className="table-inline-loading" style={{color:'#888'}}>No more jobs.</li>
            )}
          </ul>
        </div>
        <div className="table-footer">
          <Link to="/jobTablePage" className="view-more-btn">View More →</Link>
        </div>
      </div>

      {/* ✅ Latest Results */}
      <div className="table-card">
        <Link to="/resultTablePage" className="table-header-link">
          <div className="table-header">Result</div>
        </Link>
        <div
          className="table-content scroll-box"
          ref={resultsScrollRef}
          onScroll={(e) => handleTableScroll("results", e.currentTarget)}
        >
          <ul>
            {results.map((item, i) => (
              <li key={i} className="table-list-item table-list-item-detailed">
                <strong>{getJobTitle(item.jobSlug)}</strong>
                {" — "}
                <Link to={`/mform/${getMFormSlug(item)}`} className="table-inline-link">
                  View Result
                </Link>
              </li>
            ))}
            {inlineLoadingBySection.results && (
              <li className="table-inline-loading">Loading more results...</li>
            )}
            {!threeTableHasMore.results && !inlineLoadingBySection.results && results.length > 0 && (
              <li className="table-inline-loading" style={{color:'#888'}}>No more results.</li>
            )}
          </ul>
        </div>
        <div className="table-footer">
          <Link to="/resultTablePage" className="view-more-btn">View More →</Link>
        </div>
      </div>

      {/* ✅ Admit Cards */}
      <div className="table-card">
        <Link to="/admitCardTablePage" className="table-header-link">
          <div className="table-header">Admit Card</div>
        </Link>
        <div
          className="table-content scroll-box"
          ref={admitCardsScrollRef}
          onScroll={(e) => handleTableScroll("admitCards", e.currentTarget)}
        >
          <ul>
            {admitCards.map((item, i) => (
              <li key={i} className="table-list-item table-list-item-detailed">
                <strong>{getJobTitle(item.jobSlug)}</strong>
                {" — "}
                <Link to={`/mform/${getMFormSlug(item)}`} className="table-inline-link">
                  View Admit Card
                </Link>
              </li>
            ))}
            {inlineLoadingBySection.admitCards && (
              <li className="table-inline-loading">Loading more admit cards...</li>
            )}
            {!threeTableHasMore.admitCards && !inlineLoadingBySection.admitCards && admitCards.length > 0 && (
              <li className="table-inline-loading" style={{color:'#888'}}>No more admit cards.</li>
            )}
          </ul>
        </div>
        <div className="table-footer">
          <Link to="/admitCardTablePage" className="view-more-btn">View More →</Link>
        </div>
      </div>

      {/* ✅ Answer Key AdminPanel */}
      <div className="table-card">
        <Link to="/answerKeyTablePage" className="table-header-link">
          <div className="table-header">Answer Key</div>
        </Link>
        <div
          className="table-content scroll-box"
          ref={answerKeysScrollRef}
          onScroll={(e) => handleTableScroll("answerKeys", e.currentTarget)}
        >
          <ul>
            {answerKeys.map((item, i) => (
              <li key={i} className="table-list-item table-list-item-detailed">
                <strong>{getJobTitle(item.jobSlug)}</strong>
                {" — "}
                <Link to={`/mform/${getMFormSlug(item)}`} className="table-inline-link">
                  View Answer
                </Link>
              </li>
            ))}
            {inlineLoadingBySection.answerKeys && (
              <li className="table-inline-loading">Loading more answer keys...</li>
            )}
            {!threeTableHasMore.answerKeys && !inlineLoadingBySection.answerKeys && answerKeys.length > 0 && (
              <li className="table-inline-loading" style={{color:'#888'}}>No more answer keys.</li>
            )}
          </ul>
        </div>
        <div className="table-footer">
          <Link to="/answerKeyTablePage" className="view-more-btn">View More →</Link>
        </div>
      </div>

      {/* ✅ Syllabus AdminPanel */}
      <div className="table-card">
        <Link to="/syllabusTablePage" className="table-header-link">
          <div className="table-header">Syllabus</div>
        </Link>
        <div
          className="table-content scroll-box"
          ref={syllabusesScrollRef}
          onScroll={(e) => handleTableScroll("syllabuses", e.currentTarget)}
        >
          <ul>
            {syllabuses.map((item, i) => (
              <li key={i} className="table-list-item table-list-item-detailed">
                <strong>{item.title || getJobTitle(item.jobSlug)}</strong>
                {" — "}
                <Link to={`/mform/${getMFormSlug(item)}`} className="table-inline-link">
                  View Syllabus
                </Link>
              </li>
            ))}
            {inlineLoadingBySection.syllabuses && (
              <li className="table-inline-loading">Loading more syllabuses...</li>
            )}
            {!threeTableHasMore.syllabuses && !inlineLoadingBySection.syllabuses && syllabuses.length > 0 && (
              <li className="table-inline-loading" style={{color:'#888'}}>No more syllabuses.</li>
            )}
          </ul>
        </div>
        <div className="table-footer">
          <Link to="/syllabusTablePage" className="view-more-btn">View More →</Link>
        </div>
      </div>

      {/* ✅ Admission AdminPanel */}
      <div className="table-card">
        <Link to="/admissionTablePage" className="table-header-link">
          <div className="table-header">Admission</div>
        </Link>
        <div
          className="table-content scroll-box"
          ref={admissionsScrollRef}
          onScroll={(e) => handleTableScroll("admissions", e.currentTarget)}
        >
          <ul>
            {admissions.map((item, i) => (
              <li key={i} className="table-list-item table-list-item-detailed">
                <strong>{item.title || getJobTitle(item.jobSlug || item.slug)}</strong>
                {" — "}
                {item.admissionLink ? (
                  <a href={item.admissionLink} target="_blank" rel="noopener noreferrer" className="table-inline-link">
                    View Admission
                  </a>
                ) : (
                  <Link to={`/mform/${item.jobSlug || item.slug}`} className="table-inline-link">
                    View Admission
                  </Link>
                )}
              </li>
            ))}
            {inlineLoadingBySection.admissions && (
              <li className="table-inline-loading">Loading more admissions...</li>
            )}
            {!threeTableHasMore.admissions && !inlineLoadingBySection.admissions && admissions.length > 0 && (
              <li className="table-inline-loading" style={{color:'#888'}}>No more admissions.</li>
            )}
          </ul>
        </div>
        <div className="table-footer">
          <Link to="/admissionTablePage" className="view-more-btn">View More →</Link>
        </div>
      </div>
    </div>
  );
}

export default ThreeTableSection;
